import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";
import {
  MapPin,
  DollarSign,
  TrendingUp,
  Calendar,
  User,
  FileText,
  Camera,
  Video,
  Building2,
  Target,
  Wrench,
  ChevronLeft,
  ChevronRight,
  X,
  Lock,
  Shield,
  Clock,
  Download,
  Home,
  Maximize2,
  Play,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { useState, useEffect } from "react";
import NDASignModal from "./NDASignModal";
import ReserveDealModal from "./ReserveDealModal";
import DocumentChecklistModal from "./DocumentChecklistModal";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import useDealDocuments from "../hooks/useDealDocuments";

const PROPERTY_TYPE_LABELS = {
  single_family: "Single Family",
  multi_family: "Multi-Family",
  apartment: "Apartment",
  commercial: "Commercial",
  land: "Land",
  mixed_use: "Mixed Use",
};

const DEAL_TYPE_LABELS = {
  flip: "Flip",
  rental: "Rental",
  development: "Development",
};

const DEAL_TYPE_COLORS = {
  flip: "bg-orange-500 text-white",
  rental: "bg-blue-500 text-white",
  development: "bg-purple-500 text-white",
};

// Photo categories organized by section
const PHOTO_SECTIONS = {
  exterior: {
    label: "Exterior",
    icon: Home,
    keys: [
      "front_exterior",
      "left_exterior",
      "right_exterior",
      "backyard",
      "garage",
    ],
  },
  interior: {
    label: "Interior",
    icon: Building2,
    keys: ["living_room", "kitchen", "bathroom", "bedroom"],
  },
  damage: {
    label: "Damage",
    icon: Wrench,
    keys: ["damage_areas"],
  },
  neighborhood: {
    label: "Neighborhood",
    icon: MapPin,
    keys: ["neighborhood_street"],
  },
};

const PHOTO_LABELS = {
  neighborhood_street: "Neighborhood Street",
  front_exterior: "Front Exterior",
  living_room: "Living Room",
  kitchen: "Kitchen",
  bathroom: "Bathroom",
  bedroom: "Bedroom",
  left_exterior: "Left Exterior",
  right_exterior: "Right Exterior",
  backyard: "Backyard",
  garage: "Garage",
  damage_areas: "Damage Areas",
};

const VIDEO_SECTIONS = {
  exterior: {
    label: "Exterior",
    keys: ["street_video", "exterior_video"],
  },
  interior: {
    label: "Interior",
    keys: ["interior_video"],
  },
  damage: {
    label: "Damage",
    keys: ["damage_video"],
  },
};

const VIDEO_LABELS = {
  street_video: "Street Video",
  exterior_video: "Exterior Video",
  interior_video: "Interior Video",
  damage_video: "Damage Video",
};

// Fullscreen Image Viewer Component
const FullscreenViewer = ({ images, initialIndex, onClose, getPhotoUrl }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        e.preventDefault();
        onClose();
      }
      if (e.key === "ArrowLeft")
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
      if (e.key === "ArrowRight")
        setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    document.addEventListener("keydown", handleKeyDown, true);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.body.style.overflow = "auto";
    };
  }, [images, onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors z-10"
        data-testid="fullscreen-close-btn"
      >
        <X className="w-6 h-6" />
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
        }}
        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors"
        data-testid="fullscreen-prev-btn"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          setCurrentIndex((prev) => (prev + 1) % images.length);
        }}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors"
        data-testid="fullscreen-next-btn"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      <div
        className="max-w-[90vw] max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={getPhotoUrl(images[currentIndex])}
          alt={PHOTO_LABELS[images[currentIndex]] || images[currentIndex]}
          className="max-w-full max-h-[85vh] object-contain"
        />
        <div className="text-center mt-4">
          <p className="text-white text-lg">
            {PHOTO_LABELS[images[currentIndex]] || images[currentIndex]}
          </p>
          <p className="text-white/60 text-sm">
            {currentIndex + 1} / {images.length}
          </p>
        </div>
      </div>
    </div>
  );
};

const DealDetailModal = ({ deal, open, onOpenChange, onDealUpdate }) => {
  const { user } = useAuth();
  const { documents: dealDocumentTypes, loadingDocs: loadingDocTypes } = useDealDocuments();
  const [showNDAModal, setShowNDAModal] = useState(false);
  const [hasSignedNDA, setHasSignedNDA] = useState(false);
  const [checkingNDA, setCheckingNDA] = useState(true);
  const [dealNdaUrl, setDealNdaUrl] = useState(null);
  const [dealNdaS3Key, setDealNdaS3Key] = useState(null);
  const [fullscreenViewer, setFullscreenViewer] = useState(null);
  const [activePhotoTab, setActivePhotoTab] = useState("exterior");

  // Reservation states
  const [showReserveModal, setShowReserveModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [reserving, setReserving] = useState(false);
  const [localDeal, setLocalDeal] = useState(deal);

  useEffect(() => {
    if (deal) {
      setLocalDeal(deal);
    }
  }, [deal]);

  useEffect(() => {
    if (deal && open) {
      checkNDAStatus();
      fetchDealNdaUrl();
    }
  }, [deal, open]);

  const fetchDealNdaUrl = async () => {
    try {
      const res = await axios.get(`${API}/settings`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      if (res.data?.deal_nda) setDealNdaUrl(res.data.deal_nda);
      // store raw key from deal.nda_signed if available (set by admin on assignment)
    } catch {}
  };

  // Helper to get auth headers from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const checkNDAStatus = async () => {
    if (!deal) return;

    setCheckingNDA(true);
    try {
      const response = await axios.get(`${API}/nda/check/${deal.deal_id}`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      setHasSignedNDA(response.data.signed);
      if (!response.data.signed) {
        setShowNDAModal(true);
      }
    } catch (error) {
      const signedNDAs = JSON.parse(localStorage.getItem("signedNDAs") || "{}");
      const signed = !!signedNDAs[deal.deal_id];
      setHasSignedNDA(signed);
      if (!signed) {
        setShowNDAModal(true);
      }
    } finally {
      setCheckingNDA(false);
    }
  };

  const handleNDASign = async ({ legalName, s3Key }) => {
    try {
      await axios.post(
        `${API}/nda/sign`,
        {
          deal_id: deal.deal_id,
          legal_name: legalName,
          s3_key: s3Key || null,
        },
        {
          withCredentials: true,
          headers: getAuthHeaders(),
        },
      );
    } catch (error) {
      const signedNDAs = JSON.parse(localStorage.getItem("signedNDAs") || "{}");
      signedNDAs[deal.deal_id] = { signed_at: new Date().toISOString() };
      localStorage.setItem("signedNDAs", JSON.stringify(signedNDAs));
    }
    setHasSignedNDA(true);
    setShowNDAModal(false);
  };

  const handleReserveDeal = async () => {
    if (!localDeal) return;

    setReserving(true);
    try {
      const response = await axios.post(
        `${API}/deals/${localDeal.deal_id}/reserve`,
        {},
        { withCredentials: true, headers: getAuthHeaders() },
      );

      // Update local deal state
      setLocalDeal((prev) => ({
        ...prev,
        status: "reserved",
        reserved_by: response.data.reserved_by,
        reserved_by_name: response.data.reserved_by_name,
        reserved_at: response.data.reserved_at,
        documents_signed: response.data.documents_signed,
      }));

      toast.success(
        "Deal reserved successfully! Please complete the document signing.",
      );
      setShowReserveModal(false);
      setShowDocumentModal(true);

      // Notify parent
      if (onDealUpdate) {
        onDealUpdate();
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to reserve deal";
      toast.error(message);
    } finally {
      setReserving(false);
    }
  };

  const handleDocumentUpdate = (data) => {
    setLocalDeal((prev) => ({
      ...prev,
      documents_signed: data.documents_signed,
      status: data.status,
    }));
    if (onDealUpdate) {
      onDealUpdate();
    }
  };

  // Determine reservation status
  const isReserved =
    localDeal?.reserved_by !== null && localDeal?.reserved_by !== undefined;
  const isReservedByMe = localDeal?.reserved_by === user?.user_id;
  const isReservedByOther = isReserved && !isReservedByMe;

  // Handle NDA modal close - only close parent if user cancels (not signed)
  const handleNDAModalClose = (isOpen) => {
    // If NDA modal is being closed and user hasn't signed, close the parent modal too
    if (!isOpen && !hasSignedNDA) {
      onOpenChange(false);
    }
    // Only update NDA modal state if we're opening it or if user hasn't signed yet
    if (isOpen || !hasSignedNDA) {
      setShowNDAModal(isOpen);
    }
  };

  if (!deal || !localDeal) return null;

  const profit =
    localDeal.estimated_arv -
    localDeal.asking_price -
    localDeal.estimated_rehab_cost;
  const roi = ((profit / localDeal.asking_price) * 100).toFixed(1);

  const formatCurrency = (value) => {
    if (!value) return "$0";
    return "$" + value.toLocaleString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getPhotoUrl = (key) => {
    if (localDeal.photos?.[key]?.url) {
      return localDeal.photos[key].url;
    }
    const colors = { flip: "F97316", rental: "3B82F6", development: "8B5CF6" };
    const color = colors[localDeal.deal_type] || "64748B";
    const label = PHOTO_LABELS[key] || key;
    return `https://placehold.co/800x600/${color}/FFFFFF?text=${encodeURIComponent(label)}`;
  };

  const getPhotosForSection = (sectionKey) => {
    const section = PHOTO_SECTIONS[sectionKey];
    if (!section) return [];
    return section.keys;
  };

  const getVideosForSection = (sectionKey) => {
    const section = VIDEO_SECTIONS[sectionKey];
    if (!section) return [];
    return section.keys.filter((key) => localDeal.videos?.[key]);
  };

  // Loading state
  if (checkingNDA) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogTitle className="sr-only">Checking deal access</DialogTitle>
          <DialogDescription className="sr-only">
            Checking whether you have access to view this deal.
          </DialogDescription>
          <div className="py-12 text-center">
            <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
              <Shield className="w-8 h-8 text-blue-500" />
            </div>
            <p className="text-muted-foreground">Checking access...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // NDA not signed
  if (!hasSignedNDA) {
    return (
      <>
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogTitle className="sr-only">NDA required</DialogTitle>
            <DialogDescription className="sr-only">
              Sign the non-disclosure agreement to view this deal.
            </DialogDescription>
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-orange-500" />
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                NDA Required
              </h3>
              <p className="text-muted-foreground mb-4">
                Please sign the Non-Disclosure Agreement to view this deal.
              </p>
              <Button
                onClick={() => setShowNDAModal(true)}
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="open-nda-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                View & Sign NDA
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <NDASignModal
          open={showNDAModal}
          onOpenChange={handleNDAModalClose}
          onSignComplete={handleNDASign}
          pdfUrl={dealNdaUrl}
          s3Key={deal?.nda_signed?.s3_key || null}
          requireNameMatch={true}
          mode="deal"
          onDecline={() => {
            setShowNDAModal(false);
            onOpenChange(false);
          }}
        />
      </>
    );
  }

  // Full deal details (NDA signed)
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[1000px] max-h-[95vh] overflow-y-auto p-0">
          <DialogTitle className="sr-only">Deal details</DialogTitle>
          <DialogDescription className="sr-only">
            View detailed property, financial, media, reservation, and document
            information for this deal.
          </DialogDescription>
          {/* Header */}
          <div className="sticky top-0 z-10 bg-card border-b px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge className="bg-green-500/90 text-white border-0">
                <Shield className="w-3 h-3 mr-1" />
                NDA Signed
              </Badge>
              {localDeal?.nda_signed?.signed && dealNdaUrl && (
                <a
                  href={dealNdaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-500 hover:underline"
                >
                  <ExternalLink className="w-3 h-3" />
                  View Signed NDA
                </a>
              )}
              {isReservedByMe && (
                <Badge className="bg-blue-500/90 text-white border-0">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Reserved by You
                </Badge>
              )}
              {isReservedByOther && (
                <Badge className="bg-orange-500/90 text-white border-0">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Under Contract
                </Badge>
              )}
              {localDeal?.status === "in_closing" && (
                <Badge className="bg-purple-500/90 text-white border-0">
                  <Clock className="w-3 h-3 mr-1" />
                  In Closing
                </Badge>
              )}
              <Badge className={DEAL_TYPE_COLORS[localDeal?.deal_type]}>
                {DEAL_TYPE_LABELS[localDeal?.deal_type] || localDeal?.deal_type}
              </Badge>
              <Badge variant="outline">
                {PROPERTY_TYPE_LABELS[localDeal?.property_type] ||
                  localDeal?.property_type}
              </Badge>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-8">
            {/* SECTION A - Key Numbers */}
            <section data-testid="section-key-numbers">
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <Card className="bg-blue-500/5 border-blue-500/20">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2 text-blue-500 mb-2">
                      <DollarSign className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase">
                        Purchase Price
                      </span>
                    </div>
                    <p className="text-2xl font-bold font-heading text-foreground">
                      {formatCurrency(localDeal.asking_price)}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-orange-500/5 border-orange-500/20">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2 text-orange-500 mb-2">
                      <Wrench className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase">
                        Rehab Cost
                      </span>
                    </div>
                    <p className="text-2xl font-bold font-heading text-foreground">
                      {formatCurrency(localDeal.estimated_rehab_cost)}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-purple-500/5 border-purple-500/20">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2 text-purple-500 mb-2">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase">ARV</span>
                    </div>
                    <p className="text-2xl font-bold font-heading text-foreground">
                      {formatCurrency(localDeal.estimated_arv)}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-green-500/5 border-green-500/20">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2 text-green-500 mb-2">
                      <DollarSign className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase">
                        Est. Profit
                      </span>
                    </div>
                    <p className="text-2xl font-bold font-heading text-green-600 dark:text-green-400">
                      {formatCurrency(profit)}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-emerald-500/5 border-emerald-500/20">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2 text-emerald-500 mb-2">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase">ROI</span>
                    </div>
                    <p className="text-2xl font-bold font-heading text-emerald-600 dark:text-emerald-400">
                      {roi}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Timeline & Badges Row */}
              <div className="flex items-center gap-4 mt-4 flex-wrap">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <span>Close of Escrow:</span>
                  <span className="font-medium text-foreground">
                    30-45 Days
                  </span>
                </div>
                <Separator orientation="vertical" className="h-4" />
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span>Listed:</span>
                  <span className="font-medium text-foreground">
                    {formatDate(localDeal.created_at)}
                  </span>
                </div>
                {localDeal.wholesale_fee && (
                  <>
                    <Separator orientation="vertical" className="h-4" />
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">
                        Wholesale Fee:
                      </span>
                      <span className="font-medium text-purple-500">
                        {formatCurrency(localDeal.wholesale_fee)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </section>

            <Separator />

            {/* SECTION B - Property Overview */}
            <section data-testid="section-property-overview">
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Property Overview
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Property Details */}
                <div className="space-y-4">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Full Address
                        </p>
                        <p className="font-medium text-foreground text-lg">
                          {localDeal.property_address}
                        </p>
                        <p className="text-muted-foreground">
                          {localDeal.city}, {localDeal.state}{" "}
                          {localDeal.zip_code}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted/50 rounded-lg p-4">
                      <p className="text-sm text-muted-foreground mb-1">
                        Property Type
                      </p>
                      <p className="font-medium text-foreground">
                        {PROPERTY_TYPE_LABELS[localDeal.property_type] ||
                          localDeal.property_type}
                      </p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4">
                      <p className="text-sm text-muted-foreground mb-1">
                        Deal Type
                      </p>
                      <p className="font-medium text-foreground">
                        {DEAL_TYPE_LABELS[localDeal.deal_type] ||
                          localDeal.deal_type}
                      </p>
                    </div>
                  </div>

                  {/* Property Specs */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-muted/50 rounded-lg p-4">
                      <p className="text-sm text-muted-foreground mb-1">
                        Bedrooms
                      </p>
                      <p className="font-medium text-foreground">
                        {localDeal.bedrooms ?? "N/A"}
                      </p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4">
                      <p className="text-sm text-muted-foreground mb-1">
                        Bathrooms
                      </p>
                      <p className="font-medium text-foreground">
                        {localDeal.bathrooms ?? "N/A"}
                      </p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4">
                      <p className="text-sm text-muted-foreground mb-1">
                        Sq. Footage
                      </p>
                      <p className="font-medium text-foreground">
                        {localDeal.square_footage
                          ? localDeal.square_footage.toLocaleString() + " sqft"
                          : "N/A"}
                      </p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4">
                      <p className="text-sm text-muted-foreground mb-1">
                        Lot Size
                      </p>
                      <p className="font-medium text-foreground">
                        {localDeal.lot_size || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Column - Scope of Work */}
                <div className="space-y-4">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Wrench className="w-5 h-5 text-muted-foreground" />
                      <p className="font-medium text-foreground">
                        Scope of Work
                      </p>
                    </div>
                    {localDeal.repair_notes ? (
                      <p className="text-muted-foreground leading-relaxed">
                        {localDeal.repair_notes}
                      </p>
                    ) : (
                      <p className="text-muted-foreground italic">
                        No repair notes provided
                      </p>
                    )}
                  </div>

                  {localDeal.additional_notes && (
                    <div className="bg-muted/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <p className="font-medium text-foreground">
                          Additional Notes
                        </p>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        {localDeal.additional_notes}
                      </p>
                    </div>
                  )}

                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-5 h-5 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Submitted By
                      </p>
                    </div>
                    <p className="font-medium text-foreground">
                      {localDeal.submitted_by_name || "Anonymous"}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <Separator />

            {/* SECTION C - Photo Gallery */}
            <section data-testid="section-photos">
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Photo Gallery
              </h3>

              <Tabs value={activePhotoTab} onValueChange={setActivePhotoTab}>
                <TabsList className="mb-4">
                  {Object.entries(PHOTO_SECTIONS).map(([key, section]) => {
                    const Icon = section.icon;
                    return (
                      <TabsTrigger key={key} value={key} className="gap-2">
                        <Icon className="w-4 h-4" />
                        {section.label}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {Object.entries(PHOTO_SECTIONS).map(([sectionKey, section]) => (
                  <TabsContent key={sectionKey} value={sectionKey}>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {section.keys.map((photoKey, index) => (
                        <div
                          key={photoKey}
                          className="relative group cursor-pointer rounded-lg overflow-hidden aspect-[4/3] bg-muted"
                          onClick={() =>
                            setFullscreenViewer({ images: section.keys, index })
                          }
                        >
                          <img
                            src={getPhotoUrl(photoKey)}
                            alt={PHOTO_LABELS[photoKey]}
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                            <Maximize2 className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                            <p className="text-white text-sm font-medium">
                              {PHOTO_LABELS[photoKey]}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </section>

            <Separator />

            {/* SECTION D - Videos */}
            <section data-testid="section-videos">
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                <Video className="w-5 h-5" />
                Videos
              </h3>

              <Tabs defaultValue="exterior">
                <TabsList className="mb-4">
                  {Object.entries(VIDEO_SECTIONS).map(([key, section]) => (
                    <TabsTrigger key={key} value={key}>
                      {section.label}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {Object.entries(VIDEO_SECTIONS).map(([sectionKey, section]) => (
                  <TabsContent key={sectionKey} value={sectionKey}>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {section.keys.map((videoKey) => {
                        const hasVideo = localDeal.videos?.[videoKey];
                        return (
                          <div
                            key={videoKey}
                            className={`relative rounded-lg overflow-hidden aspect-video bg-muted ${
                              hasVideo ? "cursor-pointer" : "opacity-50"
                            }`}
                          >
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                              <div
                                className={`w-16 h-16 rounded-full flex items-center justify-center ${
                                  hasVideo
                                    ? "bg-blue-500"
                                    : "bg-muted-foreground/20"
                                }`}
                              >
                                <Play
                                  className={`w-8 h-8 ${hasVideo ? "text-white" : "text-muted-foreground"}`}
                                />
                              </div>
                              <p className="mt-3 text-sm font-medium text-foreground">
                                {VIDEO_LABELS[videoKey]}
                              </p>
                              {!hasVideo && (
                                <p className="text-xs text-muted-foreground">
                                  Not uploaded
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </section>

            <Separator />

            {/* SECTION E - Documents */}
            <section data-testid="section-documents">
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Documents
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {loadingDocTypes ? (
                  <div className="col-span-2 flex items-center gap-2 text-muted-foreground py-4">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Loading documents...</span>
                  </div>
                ) : dealDocumentTypes.map((doc) => {
                  const hasDocument = localDeal.documents?.[doc.name];
                  const Icon = FileText;
                  return (
                    <div
                      key={doc.name}
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        hasDocument
                          ? "bg-card border-border hover:border-blue-500/50 transition-colors"
                          : "bg-muted/30 border-dashed opacity-60"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            hasDocument ? "bg-blue-500/10" : "bg-muted"
                          }`}
                        >
                          <Icon
                            className={`w-5 h-5 ${hasDocument ? "text-blue-500" : "text-muted-foreground"}`}
                          />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {doc.label}
                          </p>
                          {hasDocument &&
                            localDeal.documents[doc.name].filename && (
                              <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                                {localDeal.documents[doc.name].filename}
                              </p>
                            )}
                          {!hasDocument && (
                            <p className="text-xs text-muted-foreground">
                              Not uploaded
                            </p>
                          )}
                        </div>
                      </div>
                      {hasDocument &&
                        localDeal.documents[doc.name].storage_path && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            data-testid={`download-${doc.name}`}
                            onClick={async () => {
                              try {
                                const storagePath =
                                  localDeal.documents[doc.name].storage_path;
                                const response = await axios.get(
                                  `${API}/deals/documents/presigned-url`,
                                  {
                                    params: {
                                      key: storagePath,
                                      download: true,
                                    },
                                    withCredentials: true,
                                    headers: getAuthHeaders(),
                                  },
                                );

                                const url = response.data?.url;
                                if (!url) {
                                  throw new Error("No download URL returned");
                                }

                                window.open(
                                  url,
                                  "_blank",
                                  "noopener,noreferrer",
                                );
                              } catch {
                                toast.error("Failed to download file");
                              }
                            }}
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </Button>
                        )}
                      {hasDocument &&
                        !localDeal.documents[doc.name].storage_path && (
                          <Badge variant="outline" className="text-xs">
                            Uploaded
                          </Badge>
                        )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-6 border-t sticky bottom-0 bg-background pb-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>

              {/* Show different buttons based on reservation status */}
              {isReservedByMe ? (
                <Button
                  className="flex-1 bg-purple-500 hover:bg-purple-600"
                  onClick={() => setShowDocumentModal(true)}
                  data-testid="view-documents-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {localDeal?.status === "in_closing"
                    ? "View Closing Documents"
                    : "Complete Documents"}
                </Button>
              ) : isReservedByOther ? (
                <Button
                  className="flex-1 bg-muted text-muted-foreground cursor-not-allowed"
                  disabled
                  data-testid="reserved-btn"
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Under Contract
                </Button>
              ) : (
                <Button
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                  onClick={() => setShowReserveModal(true)}
                  data-testid="reserve-deal-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Reserve / Accept Deal
                </Button>
              )}
            </div>
          </div>

          {/* Fullscreen Image Viewer - inside DialogContent to stay within focus trap */}
          {fullscreenViewer && (
            <FullscreenViewer
              images={fullscreenViewer.images}
              initialIndex={fullscreenViewer.index}
              onClose={() => setFullscreenViewer(null)}
              getPhotoUrl={getPhotoUrl}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Reserve Deal Confirmation Modal */}
      <ReserveDealModal
        open={showReserveModal}
        onOpenChange={setShowReserveModal}
        onConfirm={handleReserveDeal}
        dealAddress={localDeal?.property_address}
        loading={reserving}
      />

      {/* Document Signing Checklist Modal */}
      <DocumentChecklistModal
        open={showDocumentModal}
        onOpenChange={setShowDocumentModal}
        deal={localDeal}
        onUpdate={handleDocumentUpdate}
      />
    </>
  );
};

export default DealDetailModal;
